import { UserOutlined } from "@ant-design/icons";
import cn from "classnames";
import { CSSProperties, PropsWithChildren, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { accentFor, headerColors } from "@app/shared/lib/design/apply-design";
import { usePortalDesign } from "@app/shared/lib/design/portal-design";
import { useLoc, useSchool, useUser } from "@app/shared/lib/hooks";
import { useDevice } from "@app/shared/providers/DeviceProvider";
import { useTheme } from "@app/shared/providers/ThemeProvider";
import { Chat } from "@app/shared/ui/BasicLayout/components/Header/Chat/Chat";
import { AdminMark } from "@app/shared/ui/BasicLayout/components/Header/AdminMark/AdminMark";
import { Notifications } from "@app/shared/ui/BasicLayout/components/Header/Notifications/Notifications";
import { GamificationBalance } from "@app/shared/ui/GamificationBalance";
import { SchoolLogo } from "@app/shared/ui/SchoolLogo";
import { hasPageBackdrop, PageBackdrop, usePageBackground } from "@app/shared/ui/PageBackdrop/PageBackdrop";
import { UserAvatar } from "@app/shared/ui/UserAvatar";
import { PortalFooter } from "@app/shared/ui/PortalFooter/PortalFooter";

import { AccountDropdown, AccountSheet } from "./AccountMenu";
import { ThemeMenuButton } from "./ThemeSwitch";
import { useNavItems, type NavItem } from "./useNavItems";

import styles from "./AppShell.module.scss";

/** Inside the native app the student portal runs in a WebView that has its own bottom tabs. */
const inNativeApp = () => typeof window !== "undefined" && !!(window as unknown as { ReactNativeWebView?: unknown }).ReactNativeWebView;

const PointsPill: React.FC = () => {
  const school = useSchool();
  if (!school.gamification) return null;
  return (
    <Link to="/gamification" className={styles.pointsPill}>
      <GamificationBalance iconSize={20} />
    </Link>
  );
};

/**
 * The top menu tightens itself when it does not fit (owner, 2026-10-26: «Affiliate Program» was clipped by the
 * hidden scroll at the 1190px content width): `data-density` normal → compact → tight, measured on every resize.
 */
const useNavDensity = (vertical?: boolean) => {
  const ref = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || vertical) return;
    const fit = () => {
      for (const density of ["normal", "compact", "tight"]) {
        el.dataset.density = density;
        if (el.scrollWidth <= el.clientWidth + 1) break;
      }
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [vertical]);
  return ref;
};

const NavLinks: React.FC<{ items: NavItem[]; activeKey?: string; vertical?: boolean }> = ({ items, activeKey, vertical }) => {
  const ref = useNavDensity(vertical);
  return (
    <nav ref={ref} className={vertical ? styles.sideNavList : styles.topNav} aria-label="primary">
      {items.map((item) =>
        item.external ? (
          // the school's own link (portal design nav.items): another site, a new tab
          <a
            key={item.key}
            href={item.path}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(vertical ? styles.sideNavItem : styles.topNavItem, "school-nav-custom")}
          >
            {vertical && (
              <span className={styles.sideNavIcon} aria-hidden>
                {item.icon}
              </span>
            )}
            <span>{item.label}</span>
          </a>
        ) : (
        <NavLink
          key={item.key}
          to={item.path}
          className={cn(vertical ? styles.sideNavItem : styles.topNavItem, item.key === activeKey && styles.active)}
          aria-current={item.key === activeKey ? "page" : undefined}
        >
          {vertical && (
            <span className={styles.sideNavIcon} aria-hidden>
              {item.icon}
            </span>
          )}
          <span>{item.label}</span>
        </NavLink>
        ),
      )}
    </nav>
  );
};

/**
 * The frame of every signed-in page outside the lesson player: top bar or sidebar on desktop (portal
 * design `header.nav`; its `header.style` colours the bar), compact top bar (points, chat, notifications — finger-sized) + bottom tab bar
 * with the student's avatar as the «Profile» tab on phones and tablets.
 * Keeps the `course-header`-free markup schools style today on the page level (`material-page` etc.).
 */
export const AppShell: React.FC<PropsWithChildren> = ({ children }) => {
  const { deviceSize } = useDevice();
  const design = usePortalDesign();
  const pageBg = usePageBackground("pages");
  const school = useSchool();
  const { resolved, allowSwitch } = useTheme();
  const nav = design.header.nav;
  // coloured bar (accent / own colour): the bar's tokens are re-pointed at a readable foreground in SCSS
  const header = useMemo(
    () => headerColors(design, accentFor(design, school.primaryColor, resolved)),
    [design, school.primaryColor, resolved],
  );
  const headerProps = header
    ? { "data-header": header.tone, style: { "--axl-header-bg": header.bg, "--axl-header-fg": header.fg } as CSSProperties }
    : {};
  // the logo sits on the bar's colour: a dark bar takes the dark-theme logo (when the school uploaded one)
  const logoBg = header?.tone;
  const { items, activeKey } = useNavItems();
  const { user } = useUser();
  const loc = useLoc();
  const { pathname } = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);

  const isDesktop = deviceSize === "desktop";
  const side = isDesktop && nav === "side";
  const native = inNativeApp();
  const showTabBar = !isDesktop && !native;
  // the theme is an app-bar action on every size (phones also keep the labelled switch in the account sheet)
  const themeButton = allowSwitch;

  useEffect(() => setSheetOpen(false), [pathname]);
  useEffect(() => {
    if (isDesktop) setSheetOpen(false);
  }, [isDesktop]);

  const primary = items.filter((x) => x.primary);
  const secondary = items.filter((x) => !x.primary);

  return (
    <div className={cn(styles.shell, side && styles.withSide, showTabBar && styles.withTabBar, hasPageBackdrop(pageBg) && styles.customBg)} data-size={deviceSize} data-nav={side ? "side" : "top"}>
      <PageBackdrop bg={pageBg} />
      {/* quiet ambient decoration in the school's accent (same language as the sign-in page, much fainter) */}
      {design.background.ambient && (
        <div className={styles.ambient} aria-hidden>
          <span className={styles.ambientBlob} data-n="1" />
          <span className={styles.ambientBlob} data-n="2" />
          <svg className={styles.ambientWaves} viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path data-n="1" d="M0,192 C240,128 480,256 720,208 C960,160 1200,96 1440,160 L1440,320 L0,320 Z" />
            <path data-n="2" d="M0,240 C260,192 520,288 760,248 C1000,208 1220,176 1440,224 L1440,320 L0,320 Z" />
          </svg>
        </div>
      )}
      {side ? (
        <aside className={styles.side} {...headerProps}>
          <Link to="/" className={styles.sideLogo} aria-label={loc.word("School.nav.learning", { default: "Learning" })}>
            <SchoolLogo height={36} background={logoBg} nameFallback />
          </Link>
          <NavLinks items={items} activeKey={activeKey} vertical />
          <div className={styles.sideFooter}>
            <PointsPill />
            <div className={styles.sideActions}>
              {themeButton && <ThemeMenuButton />}
              <Chat />
              <Notifications />
              <AccountDropdown compact />
            </div>
          </div>
        </aside>
      ) : (
        <header className={styles.topBar} {...headerProps}>
          <div className={styles.topBarInner}>
            <Link to="/" className={styles.logo}>
              <SchoolLogo height={isDesktop ? 36 : 32} background={logoBg} nameFallback />
            </Link>
            {isDesktop && <NavLinks items={items} activeKey={activeKey} />}
            {isDesktop && user?.session?.adminId != null && <AdminMark session={user.session} />}
            <div className={styles.topBarActions}>
              <PointsPill />
              {themeButton && <ThemeMenuButton />}
              <Chat />
              <Notifications />
              {(isDesktop || native) && <AccountDropdown compact={!isDesktop} extraItems={isDesktop ? [] : secondary} />}
            </div>
          </div>
        </header>
      )}

      {/* phones and tablets: the plate would squeeze the bar's own controls — a strip under it instead */}
      {!side && !isDesktop && user?.session?.adminId != null && <AdminMark session={user.session} variant="banner" />}

      {side && user?.session?.adminId != null && (
        <div className={styles.adminBar}>
          <AdminMark session={user.session} />
        </div>
      )}

      <main className={styles.main}>
        {children}
        {/* portal design footer.mode = structured, where the school's «footer on pages» setting allows it */}
        <PortalFooter place="page" />
      </main>

      {showTabBar && (
        <>
          <nav className={styles.tabBar} aria-label="primary" data-tabbar>
            {primary.map((item) => (
              <NavLink
                key={item.key}
                to={item.path}
                className={cn(styles.tab, item.key === activeKey && !sheetOpen && styles.active)}
              >
                <span className={styles.tabIcon} aria-hidden>
                  {item.icon}
                </span>
                <span className={styles.tabLabel}>{item.tabLabel ?? item.label}</span>
              </NavLink>
            ))}
            <button
              type="button"
              className={cn(styles.tab, (sheetOpen || (!!activeKey && !primary.some((x) => x.key === activeKey))) && styles.active)}
              onClick={() => setSheetOpen(true)}
            >
              <span className={cn(styles.tabIcon, styles.tabAvatar)} aria-hidden>
                {user ? <UserAvatar user={user} size={26} color="var(--axl-primary)" /> : <UserOutlined />}
              </span>
              <span className={styles.tabLabel}>{user?.firstName || loc.word("Profile")}</span>
            </button>
          </nav>
          <AccountSheet open={sheetOpen} onClose={() => setSheetOpen(false)} extraItems={secondary} />
        </>
      )}
    </div>
  );
};
